import { Row, Col } from 'react-bootstrap';
import { IWatrTrip } from '@nkr/models/watr-trip';
import { ILineItem } from '../types/line-item.types';

interface CaregiverInfoCardProps {
    trip: IWatrTrip;
    lineItems?: ILineItem[];
}

interface CaregiverInfo {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string | number;
    postalCode?: string;
}

// Extended type to include optional caregiver properties that may exist at runtime
type IWatrTripWithCaregiver = IWatrTrip & {
    hasCaregiverExpense?: boolean;
    caregiverDescription?: string;
    caregiverName?: string;
    caregiverAddressLine1?: string;
    caregiverAddressLine2?: string;
    caregiverCity?: string;
    caregiverState?: string | number;
    caregiverPostalCode?: string;
};

export const CaregiverInfoCard = (props: CaregiverInfoCardProps) => {
    const trip = props.trip as IWatrTripWithCaregiver;
    const hasCaregiverLineItems = props.lineItems && props.lineItems.some(li => li.isCaregiverExpense === true);
    const hasCaregiverData = trip.hasCaregiverExpense === true || hasCaregiverLineItems;
    
    if (!hasCaregiverData) {
        return null;
    }

    const parseCaregiverInfo = (): CaregiverInfo | null => {
        if (trip.caregiverDescription) {
            try {
                const description = typeof trip.caregiverDescription === 'string' 
                    ? trip.caregiverDescription.trim() 
                    : String(trip.caregiverDescription);
                
                if (!description || description.length === 0) {
                    return null;
                }
                
                const parsed = JSON.parse(description);
                
                if (parsed && typeof parsed === 'object') {
                    return {
                        name: parsed.name || parsed.Name || null,
                        addressLine1: parsed.addressLine1 || parsed.AddressLine1 || parsed.address || null,
                        addressLine2: parsed.addressLine2 || parsed.AddressLine2 || null,
                        city: parsed.city || parsed.City || null,
                        state: parsed.state || parsed.State || null,
                        postalCode: parsed.postalCode || parsed.PostalCode || parsed.zipCode || parsed.zip || null
                    };
                }
            } catch (e) {
                console.error('Failed to parse caregiver description JSON:', e, trip.caregiverDescription);
            }
        }
        
        if (trip.caregiverName || trip.caregiverAddressLine1 || trip.caregiverCity) {
            return {
                name: trip.caregiverName || null,
                addressLine1: trip.caregiverAddressLine1 || null,
                addressLine2: trip.caregiverAddressLine2 || null,
                city: trip.caregiverCity || null,
                state: trip.caregiverState || null,
                postalCode: trip.caregiverPostalCode || null
            };
        }
        
        return null;
    };

    const caregiverInfo = parseCaregiverInfo();
    const hasAddress = caregiverInfo && (caregiverInfo.addressLine1 || caregiverInfo.city || caregiverInfo.postalCode);

    const formatCityStateZip = () => {
        if (!caregiverInfo) return null;
        const parts: string[] = [];
        if (caregiverInfo.city) parts.push(caregiverInfo.city);
        if (caregiverInfo.state) {
            parts.push(String(caregiverInfo.state));
        }
        if (caregiverInfo.postalCode) parts.push(caregiverInfo.postalCode);
        return parts.length > 0 ? parts.join(', ') : null;
    };

    return (
        <div style={{ 
            background: 'white', 
            borderRadius: '4px', 
            padding: '16px', 
            height: '100%',
            border: '1px solid #e1e8ed', 
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)' 
        }}>
            <div style={{ 
                fontSize: '12px', 
                fontWeight: 600, 
                color: '#495057', 
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                Caregiver Information
            </div>
            <Row style={{ fontSize: '13px', color: '#212529' }}>
                <Col md={6} xs={12}>
                    {caregiverInfo?.name ? (
                        <div>
                            <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                Name
                            </div>
                            <div style={{ fontWeight: 500 }}>
                                {caregiverInfo.name}
                            </div>
                        </div>
                    ) : (
                        !hasAddress && hasCaregiverLineItems && (
                            <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                                Caregiver expenses present but no caregiver information available
                            </div>
                        )
                    )}
                </Col>
                <Col md={6} xs={12}>
                    {hasAddress && caregiverInfo && (
                        <div>
                            <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                Address
                            </div>
                            <div style={{ lineHeight: '1.6' }}>
                                {caregiverInfo.addressLine1 && <div>{caregiverInfo.addressLine1}</div>}
                                {caregiverInfo.addressLine2 && <div>{caregiverInfo.addressLine2}</div>}
                                {formatCityStateZip() && <div>{formatCityStateZip()}</div>}
                            </div>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

